import { useCallback, useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import scheduleServiceApi from '../api/schedule.service.api';
import { NO_OPEN_CHECK_IN_SESSION_MESSAGE_PREFIX } from '../constants/schedule.constants';
import { httpStatus, httpMessage } from '../utils/http-status';
import {
  attendanceKeys,
  checkedInCountInterval,
  invalidateEventAttendance,
  readOpenSession,
  CHECK_IN_SESSION_STATUS,
  DEFAULT_POLL_INTERVAL_MS,
  type CheckInsPage,
  type CheckInSession,
  type CheckInSessionStatus,
  type CloseResult,
  type CloseSummary,
  type OpenResult,
  type SessionOptions,
} from './attendance.types';

/**
 * Polls the checked-in total while the screen is foregrounded. Backgrounding is
 * handled globally by the AppState → focusManager bridge (mobile/index.js); RQ
 * pauses interval refetches while unfocused by default, so the poll stops off-screen
 * and resumes on foreground. On transient failure the interval backs off (base → 2x
 * → 4x, cap 30s) and resets to base on the next success.
 */
export function useCheckedInCount(
  eventId: string | null | undefined,
  {
    intervalMs = DEFAULT_POLL_INTERVAL_MS,
    onError,
  }: { intervalMs?: number; onError?: SessionOptions['onError'] } = {},
) {
  const query = useQuery<CheckInsPage, unknown, number>({
    queryKey: attendanceKeys.checkedInCount(eventId),
    queryFn: () => scheduleServiceApi.getCheckIns({ eventId, limit: 1 }) as Promise<CheckInsPage>,
    enabled: !!eventId,
    select: page => page.total ?? 0,
    // retry:0 so the refetch loop is the sole backoff; RQ's retry would stack a second one.
    retry: 0,
    refetchInterval: q => checkedInCountInterval(intervalMs, q.state.fetchFailureCount),
  });

  useEffect(() => {
    if (query.error) onError?.(query.error, { scope: 'checked-in-count', eventId });
  }, [query.error, onError, eventId]);

  return {
    total: query.data ?? 0,
    hasLoaded: query.isSuccess,
    error: query.error ?? null,
  };
}

/**
 * Owns the host's check-in session for a single event, shared by web and mobile.
 *
 * The open session is *read* with a query (idempotent GET, safe to refetch);
 * open/close are mutations. `open()` PUTs, and on a 409 (already open) refetches
 * and reuses the existing session rather than rotating the TOTP secret — so a QR
 * already on screen stays valid. The close summary is local UI state shown once
 * after close. Errors are surfaced via `error`/the returned `{ ok }` and also
 * reported through the optional `onError` hook (hosts inject their own monitoring).
 */
export function useCheckInSession(
  event: { id?: string | null } | null | undefined,
  { enabled = true, onError }: SessionOptions = {},
) {
  const eventId = event?.id;
  const qc = useQueryClient();
  const [closedSummary, setClosedSummary] = useState<CloseSummary | null>(null);

  const sessionQuery = useQuery<CheckInSession | null>({
    queryKey: attendanceKeys.session(eventId),
    enabled: enabled && !!eventId,
    queryFn: async () => {
      try {
        return readOpenSession(await scheduleServiceApi.getCheckInSession({ eventId }));
      } catch (error) {
        const noOpenSession =
          httpStatus(error) === 404 &&
          httpMessage(error)?.startsWith(NO_OPEN_CHECK_IN_SESSION_MESSAGE_PREFIX);
        if (noOpenSession) {
          onError?.(error, { scope: 'read-check-in-session', eventId, expected: true });
          return null;
        }
        throw error;
      }
    },
  });

  const session = sessionQuery.data ?? null;

  const refreshSession = useCallback(
    () => qc.invalidateQueries({ queryKey: attendanceKeys.session(eventId) }),
    [qc, eventId],
  );

  const openMutation = useMutation<CheckInSession | null, unknown, void>({
    mutationFn: async () => {
      try {
        return readOpenSession(await scheduleServiceApi.openCheckInSession({ eventId }));
      } catch (error) {
        // Re-opening rotates the TOTP secret and invalidates the on-screen QR; on 409
        // (already open) reuse the existing session instead.
        if (httpStatus(error) !== 409) throw error;
        const refetched = await qc.fetchQuery<CheckInSession | null>({
          queryKey: attendanceKeys.session(eventId),
        });
        if (!refetched) throw new Error('check-in session reported open but not confirmed');
        return refetched;
      }
    },
    onSuccess: () => {
      refreshSession();
      invalidateEventAttendance(qc, eventId as string);
    },
  });

  const closeMutation = useMutation<CloseSummary | null, unknown, void>({
    mutationFn: async () => {
      try {
        return await scheduleServiceApi.closeCheckInSession({ eventId });
      } catch (error) {
        // 404 = already closed (success); breadcrumb so a real route/event 404 isn't masked.
        if (httpStatus(error) === 404) {
          onError?.(error, { scope: 'close-check-in-session', eventId, expected: true });
          return null;
        }
        throw error;
      }
    },
    onSuccess: () => {
      refreshSession();
      invalidateEventAttendance(qc, eventId as string);
    },
  });

  const missingEventIdError = () => new Error('check-in session action without an event id');

  const open = useCallback(async (): Promise<OpenResult> => {
    if (!eventId) return { ok: false, error: missingEventIdError() };
    try {
      await openMutation.mutateAsync();
      return { ok: true };
    } catch (error) {
      onError?.(error, { scope: 'open-check-in-session', eventId });
      return { ok: false, error };
    }
  }, [eventId, openMutation, onError]);

  const close = useCallback(async (): Promise<CloseResult> => {
    if (!eventId) return { ok: false, error: missingEventIdError() };
    try {
      const data = await closeMutation.mutateAsync();
      if (!data) return { ok: true, alreadyClosed: true };
      const summary: CloseSummary = {
        openedAt: data.openedAt,
        closedAt: data.closedAt,
        totalCheckIns: data.totalCheckIns ?? 0,
        inPersonCount: data.inPersonCount ?? 0,
        virtualCount: data.virtualCount ?? 0,
      };
      setClosedSummary(summary);
      return { ok: true, summary };
    } catch (error) {
      onError?.(error, { scope: 'close-check-in-session', eventId });
      return { ok: false, error };
    }
  }, [eventId, closeMutation, onError]);

  const clearClosedSummary = useCallback(() => setClosedSummary(null), []);

  const status: CheckInSessionStatus = sessionQuery.isError
    ? CHECK_IN_SESSION_STATUS.ERROR
    : session
      ? CHECK_IN_SESSION_STATUS.OPEN
      : CHECK_IN_SESSION_STATUS.IDLE;

  return {
    session,
    isOpen: !!session,
    status,
    loaded: !enabled || sessionQuery.isSuccess,
    error: sessionQuery.isError ? sessionQuery.error : null,
    pending: openMutation.isPending || closeMutation.isPending,
    closedSummary,
    clearClosedSummary,
    open,
    close,
  };
}

useCheckInSession.STATUS = CHECK_IN_SESSION_STATUS;
